import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { FaSolarPanel, FaChartLine } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const CustomTooltip = ({ active, payload, label, chartType }) => {
  if (!active || !payload || !payload.length) return null;

  const labelColorClass =
    chartType === "area" ? "text-blue-300" : "text-green-300";

  return (
    <div
      className={`bg-gray-900/95 border-0 rounded-lg shadow-lg p-3 text-sm font-medium`}
    >
      <p className={`${labelColorClass} mb-1.5 font-semibold`}>{label}</p>
      {payload.map((entry, index) => (
        <div
          key={index}
          className="text-gray-100 flex items-center justify-between gap-4"
        >
          <span>{entry.name}:</span>
          <span className="font-bold">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  label: PropTypes.string,
  chartType: PropTypes.oneOf(["area", "bar"]).isRequired,
};

function DataVisuals() {
  const { t } = useTranslation();
  const dataVisuals =
    t("components.solution.dataVisuals", { returnObjects: true }) || {};
  const {
    sectionTitle = "Data Visualization",
    sectionSubtitle = "Visualizing your energy data",
    energyProductionTitle = "Energy Production",
    costSavingsTitle = "Cost Savings",
    energyProductionType = "kWh",
    costSavingsType = "USD",
    monthlyData = [],
    savingsData = [],
  } = dataVisuals;

  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    let timeoutId;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsLargeScreen(window.innerWidth >= 1024);
      }, 200);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const renderCustomXAxisTick = useCallback(
    ({ x, y, payload }) => {
      const monthName = payload.value;
      const displayText = isLargeScreen
        ? monthName.slice(0, 3)
        : monthName.slice(0, 1);

      return (
        <g transform={`translate(${x},${y})`}>
          <text x={0} y={0} dy={16} textAnchor="middle" fill="#D1D5DB">
            <title>{monthName}</title>
            {displayText}
          </text>
        </g>
      );
    },
    [isLargeScreen]
  );

  return (
    <section
      className="py-20 bg-black text-gray-100 overflow-hidden"
      id="safePad"
    >
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-100 mb-4">
            {sectionTitle}
          </h2>
          <div className="w-32 h-1.5 bg-linear-to-r from-blue-500 to-green-500 mx-auto mb-8 rounded-full" />
          <p className="text-sm lg:text-md text-gray-300">{sectionSubtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gray-800 p-6 rounded-2xl shadow-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <FaSolarPanel className="text-blue-400 w-6 h-6" />
              <h3 className="text-sm md:text-lg font-bold text-gray-100">
                {energyProductionTitle}
              </h3>
            </div>
            <div className="h-[350px] overflow-x-hidden">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={monthlyData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                  aria-label="Monthly energy production chart"
                >
                  <defs>
                    <linearGradient
                      id="colorProduction"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#0088FE" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#0088FE" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis
                    dataKey="month"
                    stroke="#D1D5DB"
                    tick={renderCustomXAxisTick}
                    interval={0}
                    aria-label="Months"
                  />
                  <YAxis stroke="#D1D5DB" aria-label="Energy production" />
                  <Tooltip
                    content={<CustomTooltip chartType="area" />}
                    cursor={{ fill: "rgba(59, 130, 246, 0.1)" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="production"
                    name={energyProductionType}
                    stroke="#61dafb"
                    fillOpacity={1}
                    fill="url(#colorProduction)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-gray-800 p-6 rounded-2xl shadow-xl"
          >
            <div className="flex items-center gap-3 mb-6">
              <FaChartLine className="text-green-400 w-6 h-6" />
              <h3 className="text-sm md:text-lg font-bold text-gray-100">
                {costSavingsTitle}
              </h3>
            </div>
            <div className="h-[350px] overflow-x-hidden">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={savingsData}
                  margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                  aria-label="Yearly cost savings chart"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
                  <XAxis
                    dataKey="year"
                    stroke="#D1D5DB"
                    interval={0}
                    aria-label="Years"
                  />
                  <YAxis stroke="#D1D5DB" aria-label="Cost savings" />
                  <Tooltip
                    content={<CustomTooltip chartType="bar" />}
                    cursor={{ fill: "rgba(16, 185, 129, 0.1)" }}
                  />
                  <Bar
                    dataKey="savings"
                    name={costSavingsType}
                    fill="#10B981"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default DataVisuals;
